/**
 * NetAssetsChart — net assets since inception, with capital marked
 * ================================================================
 * Usability review of 23 October 2026, item 18: *"The dotted "Marked NAV"
 * line is a small tangle in the top-right corner. The axis goes negative. Let
 * the user turn each line on and off. Add hover values, and mark capital-in
 * and capital-out events on the line so jumps can be explained."*
 *
 * Brian, 21 September 2026: *"I want the entire history since inception"*.
 * The ledger has it; market prices exist only from September 2026. So there
 * are two lines and they are never joined — one switching from the other
 * would put a step in the chart on the day the second begins (§0.31).
 *
 * **The marked line covers six weeks of a two-and-a-half-year chart**, which
 * is what made it a tangle. It shows by default only when the period on
 * screen gives it room, its legend says the dates it covers either way, and
 * a click overrides the default in both directions.
 */
import React, { useMemo, useState } from "react";
import {
  Area, CartesianGrid, ComposedChart, Line, ReferenceDot, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import FinTable from "../../components/FinTable";
import { ExportCsvButton, InfoTip } from "../../components/PageTools";
import { compactMoney, day, money } from "../../components/format";
import {
  CAPITAL_FLOOR_USD, CAPITAL_SHARE, PERIODS, capitalEvents, chartRows, inPeriod, valueAxis,
} from "./navLogic";

// Both pass the palette validator against the card surface (lightness band,
// chroma, colour-blind separation, 3:1 contrast). The ledger line is the
// theme's own amber; the marked line a blue far enough from it in every kind
// of colour vision.
export const LEDGER_COLOUR = "#B3762E";
export const MARKED_COLOUR = "#3D6CA6";
const IN_COLOUR = "#5B8A5E";
const OUT_COLOUR = "#B8564B";
const INK_MUTED = "#8A8078";
const GRID = "#EDE8E2";

const whole = (v) => money(v, { whole: true });
const signed = (v) => (v > 0 ? `+${whole(v)}` : whole(v));
const DAY_MS = 86400000;

// Below this share of the period on screen, the marked line is a squiggle
// in a corner and is left off until somebody asks for it.
const MARKED_ROOM = 0.2;

function tickLabel(span) {
  return (v) =>
    new Date(v).toLocaleDateString("en-GB", span > 200 * DAY_MS
      ? { month: "short", year: "2-digit", timeZone: "UTC" }
      : { day: "numeric", month: "short", timeZone: "UTC" });
}

function CapitalMarker({ cx, cy, payload }) {
  if (cx == null || cy == null) return null;
  const up = payload > 0;
  // A triangle pointing the way the money went, ringed in the card colour so
  // it reads over the line, and a hit area well beyond the painted mark.
  const d = up ? `M${cx},${cy - 9} L${cx + 7},${cy + 4} L${cx - 7},${cy + 4} Z`
    : `M${cx},${cy + 9} L${cx + 7},${cy - 4} L${cx - 7},${cy - 4} Z`;
  return (
    <g className="nvp-capital-marker" aria-hidden="true">
      <circle cx={cx} cy={cy} r={12} fill="transparent" />
      <path d={d} fill={up ? IN_COLOUR : OUT_COLOUR} stroke="#FFFFFF" strokeWidth={2} strokeLinejoin="round" />
    </g>
  );
}

function HoverCard({ active, payload, show }) {
  if (!active || !payload || !payload.length) return null;
  const r = payload[0].payload;
  const flows = r.change !== null;
  const capitalWord = r.capital > 0 ? "Capital in" : r.capital < 0 ? "Capital out" : "Capital in less out";
  return (
    <div className="nvp-hover">
      <div className="nvp-hover-date">{day(r.date)}</div>
      {show.ledger && (
        <div className="nvp-hover-row">
          <span className="nvp-key" style={{ background: LEDGER_COLOUR }} />
          <b>{whole(r.ledger)}</b> <span>Net assets on the ledger</span>
        </div>
      )}
      {show.marked && (
        <div className="nvp-hover-row">
          <span className="nvp-key" style={{ background: MARKED_COLOUR }} />
          <b>{r.marked === null ? "none recorded" : whole(r.marked)}</b> <span>Marked NAV</span>
        </div>
      )}
      {flows && (
        <div className="nvp-hover-flows">
          <div className="nvp-hover-sub">{r.since ? `Since ${day(r.since)}` : "Since inception"}</div>
          <div><span>Profit</span><b>{signed(r.income)}</b></div>
          <div>
            <span>{capitalWord}</span>
            <b>{r.capital === null ? "n/a" : whole(Math.abs(r.capital))}</b>
          </div>
          <div><span>Currency translation</span><b>{signed(r.currency)}</b></div>
        </div>
      )}
    </div>
  );
}

function LegendButton({ on, colour, kind, label, detail, onClick, disabled }) {
  return (
    <button type="button" className="nvp-legend-item" aria-pressed={on} onClick={onClick} disabled={disabled}>
      {kind === "markers" ? (
        <span className="nvp-legend-markers" aria-hidden="true">
          <span style={{ color: IN_COLOUR }}>▲</span>
          <span style={{ color: OUT_COLOUR }}>▼</span>
        </span>
      ) : (
        <span className="nvp-legend-line" style={{ background: colour }} aria-hidden="true" />
      )}
      <span className="nvp-legend-text">
        <span>{label}</span>
        {detail && <small>{detail}</small>}
      </span>
    </button>
  );
}

export default function NetAssetsChart({ history, asOfDate, liveNav, loading, error }) {
  const [period, setPeriod] = useState("ALL");
  const [ledgerOn, setLedgerOn] = useState(true);
  const [markedChoice, setMarkedChoice] = useState(null); // null: decided by room
  const [capitalOn, setCapitalOn] = useState(true);
  const [tableOpen, setTableOpen] = useState(false);

  const rows = useMemo(() => chartRows(history?.points, asOfDate, liveNav), [history, asOfDate, liveNav]);
  const events = useMemo(() => capitalEvents(rows), [rows]);
  const data = inPeriod(rows, period);
  const shown = new Set(data.map((r) => r.date));

  const marked = rows.filter((r) => r.marked !== null);
  const span = data.length > 1 ? data[data.length - 1].time - data[0].time : 0;
  const markedInView = data.filter((r) => r.marked !== null);
  const markedSpan = markedInView.length > 1 ? markedInView[markedInView.length - 1].time - markedInView[0].time : 0;
  const markedFits = markedInView.length > 0 && (span === 0 || markedSpan / span >= MARKED_ROOM);
  const markedOn = markedChoice ?? markedFits;
  const show = { ledger: ledgerOn, marked: markedOn && markedInView.length > 0 };
  // Dots only where they are a few dozen across the chart, not a smear.
  const markedDots = markedInView.length <= 45 && markedSpan / Math.max(span, 1) > 0.5;

  const axis = valueAxis(data.flatMap((r) => [show.ledger ? r.ledger : null, show.marked ? r.marked : null]));
  const visibleEvents = capitalOn && show.ledger ? events.filter((e) => shown.has(e.date) && e.ledger !== null) : [];
  const belowZero = data.filter((r) => (show.ledger && r.ledger < 0) || (show.marked && r.marked < 0));

  const markedRange = marked.length
    ? `${day(marked[0].date)} – ${day(marked[marked.length - 1].date)}`
    : "none recorded";
  const ledgerRange = rows.length ? `${day(rows[0].date)} – ${day(rows[rows.length - 1].date)}` : "";

  const csvColumns = [
    { label: "Date", value: (r) => r.date },
    { label: "Net assets on the ledger (USD)", value: (r) => r.ledger },
    { label: "Marked NAV (USD)", value: (r) => r.marked },
    { label: "Movement since", value: (r) => r.since || "inception" },
    { label: "Profit (USD)", value: (r) => r.income },
    { label: "Capital in less out (USD)", value: (r) => r.capital },
    { label: "Currency translation (USD)", value: (r) => r.currency },
  ];

  return (
    <section className="nvp-panel nvp-chart-panel" aria-labelledby="nvp-chart-h">
      <div className="nvp-panel-head nvp-chart-head">
        <div>
          <h2 id="nvp-chart-h">
            Net assets over time{" "}
            <InfoTip wide label="What the two lines are">
              <p>
                <b>Net assets on the ledger</b> is what the fund’s books hold at each date, less what it owes. It is exact
                at every date back to the first entry, and it carries investments at cost with no market mark.
              </p>
              <p>
                <b>Marked NAV</b> is the figure at the top of this page, which prices listed shares at the market. It has
                been recorded daily only since {marked.length ? day(marked[0].date) : "recently"}; older prices do not
                exist, so it cannot be drawn further back.
              </p>
              <p>
                The two are measured differently and are not expected to meet. Read each line on its own rather than the
                gap between them.
              </p>
            </InfoTip>
          </h2>
          <p>US dollars, for the fund · hover the chart for values and what moved them</p>
        </div>
        <div className="nvp-periods" role="group" aria-label="Chart period">
          {PERIODS.map(([value, label]) => (
            <button key={value} type="button" aria-pressed={period === value} onClick={() => setPeriod(value)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="nvp-legend" role="group" aria-label="Lines shown on the chart">
        <LegendButton
          on={ledgerOn}
          colour={LEDGER_COLOUR}
          label="Net assets on the ledger"
          detail={ledgerRange}
          onClick={() => setLedgerOn((v) => !v)}
        />
        <LegendButton
          on={show.marked}
          colour={MARKED_COLOUR}
          label="Marked NAV"
          detail={
            !marked.length ? "none recorded"
              : !markedInView.length ? `${markedRange} · not in this period`
                : !markedOn && markedChoice === null ? `${markedRange} · off: too short for this period`
                  : markedRange
          }
          disabled={!markedInView.length}
          onClick={() => setMarkedChoice(!show.marked)}
        />
        <LegendButton
          on={capitalOn && ledgerOn}
          kind="markers"
          label="Capital in / out"
          detail={`moves of ${whole(CAPITAL_FLOOR_USD)}+ and ${CAPITAL_SHARE * 100}%+ of net assets`}
          disabled={!ledgerOn}
          onClick={() => setCapitalOn((v) => !v)}
        />
        {marked.length > 0 && !show.marked && period !== "90" && (
          <button type="button" className="pt-link nvp-legend-zoom" onClick={() => { setPeriod("90"); setMarkedChoice(true); }}>
            Show the last 3 months with marked NAV
          </button>
        )}
      </div>

      {error && !rows.length ? (
        <div className="nvp-chart nvp-empty">Could not load the history. {error}</div>
      ) : loading && !rows.length ? (
        <div className="nvp-chart nvp-empty">Loading history…</div>
      ) : !data.length || !axis ? (
        <div className="nvp-chart nvp-empty">No history in this period.</div>
      ) : (
        <div className="nvp-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 14, right: 18, bottom: 4, left: 4 }}>
              <defs>
                <linearGradient id="nvpLedgerFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={LEDGER_COLOUR} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={LEDGER_COLOUR} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke={GRID} />
              <XAxis
                dataKey="time" type="number" scale="time" domain={["dataMin", "dataMax"]}
                tickFormatter={tickLabel(span)} tick={{ fill: INK_MUTED, fontSize: 11 }}
                axisLine={{ stroke: GRID }} tickLine={false} minTickGap={40}
              />
              <YAxis
                width={64} domain={axis.domain} ticks={axis.ticks} allowDataOverflow
                tickFormatter={(v) => compactMoney(v)} tick={{ fill: INK_MUTED, fontSize: 11 }}
                axisLine={false} tickLine={false}
              />
              {axis.belowZero && <ReferenceLine y={0} stroke={INK_MUTED} strokeDasharray="3 3" />}
              <Tooltip content={<HoverCard show={show} />} cursor={{ stroke: INK_MUTED, strokeWidth: 1 }} isAnimationActive={false} />
              {show.ledger && (
                <Area
                  type="linear" dataKey="ledger" name="Net assets on the ledger" stroke={LEDGER_COLOUR} strokeWidth={2}
                  fill="url(#nvpLedgerFill)" dot={data.length === 1} activeDot={{ r: 4, stroke: "#FFFFFF", strokeWidth: 2 }}
                  isAnimationActive={false} connectNulls
                />
              )}
              {show.marked && (
                <Line
                  type="linear" dataKey="marked" name="Marked NAV" stroke={MARKED_COLOUR} strokeWidth={2}
                  dot={markedDots ? { r: 2.5, fill: MARKED_COLOUR, stroke: "#FFFFFF", strokeWidth: 1 } : false}
                  activeDot={{ r: 4, stroke: "#FFFFFF", strokeWidth: 2 }} isAnimationActive={false} connectNulls
                />
              )}
              {visibleEvents.map((e) => (
                <ReferenceDot
                  key={e.date} x={e.time} y={e.ledger} r={7}
                  shape={(props) => <CapitalMarker {...props} payload={e.capital} />}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="nvp-chart-foot">
        <span>
          {data.length} point{data.length === 1 ? "" : "s"}
          {data.length > 0 && ` · ${day(data[0].date)} – ${day(data[data.length - 1].date)}`}
          {visibleEvents.length > 0 && ` · ${visibleEvents.length} capital move${visibleEvents.length === 1 ? "" : "s"} marked`}
        </span>
        {belowZero.length > 0 && (
          <span className="nvp-below-zero">
            Below zero on {belowZero.length === 1 ? day(belowZero[0].date) : `${belowZero.length} dates`}{" "}
            <InfoTip align="right" label="Why the line goes below zero">
              <p>
                On {belowZero.length === 1 ? "this date" : "these dates"} the fund’s ledger showed it owing more than it
                held, so net assets were below nil. The axis goes below zero only as far as that.
              </p>
              {belowZero.map((r) => (
                <p key={r.date}>
                  <b>{day(r.date)}</b>: net assets {whole(r.ledger)}
                  {r.capital !== null && r.capital < 0
                    ? `, after ${whole(-r.capital)} of capital went out since ${r.since ? day(r.since) : "inception"}.`
                    : "."}
                </p>
              ))}
            </InfoTip>
          </span>
        )}
        <span className="nvp-chart-tools no-print">
          <button type="button" className="pt-link" aria-expanded={tableOpen} onClick={() => setTableOpen((v) => !v)}>
            {tableOpen ? "Hide table" : "Show as table"}
          </button>
          <ExportCsvButton filename="nav-history" columns={csvColumns} rows={data} />
        </span>
      </div>
      {tableOpen && (
        <div className="nvp-chart-table">
          <FinTable
            columns={[
              { key: "date", label: "Date", sortable: true, render: (v) => day(v) },
              { key: "ledger", label: "Net assets on the ledger", align: "right", sortable: true, render: whole },
              { key: "marked", label: "Marked NAV", align: "right", sortable: true, render: whole },
              { key: "income", label: "Profit since previous", align: "right", sortable: true, render: signed },
              { key: "capital", label: "Capital in less out", align: "right", sortable: true, render: signed },
              { key: "currency", label: "Currency translation", align: "right", sortable: true, render: signed },
            ]}
            rows={[...data].reverse()}
            pageSize={15}
          />
        </div>
      )}
    </section>
  );
}
